import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useConfig } from "@/lib/config";

/**
 * Customer-facing FAQ accordion. Driven by the same DB FAQ list that feeds the
 * FAQPage structured data (via /api/config), so the two never drift.
 */
export function FaqSection({ heading = "Frequently asked questions", className = "" }) {
  const { config } = useConfig();
  const faqs = config?.faqs || [];
  const [open, setOpen] = useState(0);

  if (!faqs.length) return null;

  return (
    <section data-testid="faq-section" className={`max-w-3xl mx-auto ${className}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight text-center">
        {heading}
      </h2>
      <div className="mt-8 divide-y divide-slate-200 dark:divide-slate-800 border-y border-slate-200 dark:border-slate-800">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`faq-item-${i}`} className="py-1">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                data-testid={`faq-question-${i}`}
                className="w-full flex items-center justify-between gap-4 py-4 text-left group"
                aria-expanded={isOpen}
              >
                <span className="text-base font-medium text-slate-900 dark:text-slate-100 group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">
                  {f.question}
                </span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`grid transition-all duration-200 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <div className="overflow-hidden">
                  <p data-testid={`faq-answer-${i}`} className="pb-5 pr-9 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {f.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default FaqSection;
